import { ChatActivityStatus } from '../services/chatbot-ia.service';
import { IMAGE_GENERATION_STATUS_LABEL } from './chatbot-generated-image';

const SKIP_TOKENS = new Set([
  'with',
  'to',
  'from',
  'by',
  'of',
  'the',
  'a',
  'an',
  'and',
]);

const VERB_TO_NOUN: Record<string, string> = {
  search: 'busca',
  get: 'consulta',
  list: 'listagem',
  find: 'consulta',
  create: 'criação',
  save: 'cadastro',
  update: 'atualização',
  patch: 'atualização',
  delete: 'exclusão',
  remove: 'remoção',
  approve: 'aprovação',
  reject: 'reprovação',
  refuse: 'recusa',
  cancel: 'cancelamento',
  toggle: 'ativação',
  send: 'envio',
  notify: 'notificação',
  mark: 'marcação',
  charge: 'cobrança',
  apply: 'aplicação',
  adjust: 'ajuste',
  fill: 'preenchimento',
  run: 'análise',
  organize: 'organização',
  confirm: 'confirmação',
  reprocess: 'reprocessamento',
  calculate: 'cotação',
  quote: 'cotação',
  browse: 'leitura',
  start: 'início',
  remember: 'gravação',
  forget: 'exclusão',
  build: 'montagem',
  consult: 'consulta',
  change: 'alteração',
  report: 'envio',
};

const TOKEN_PT: Record<string, string | null> = {
  site: 'catálogo',
  wosi: 'Wosi',
  partner: 'parceiro',
  partners: 'parceiros',
  coverage: 'cobertura',
  coverages: 'coberturas',
  client: 'cliente',
  clients: 'clientes',
  user: 'usuário',
  users: 'usuários',
  product: 'produto',
  products: 'produtos',
  voucher: 'cupom',
  vouchers: 'cupons',
  sinistro: 'sinistro',
  sinistros: 'sinistros',
  policy: 'apólice',
  profile: 'perfil',
  profiles: 'cartões',
  payment: 'pagamento',
  insurances: 'seguros',
  installments: 'parcelas',
  order: 'pedido',
  purchase: 'compra',
  mobility: 'mobilidade',
  electric: 'elétrica',
  solar: 'solares',
  panels: 'painéis',
  multimarcas: 'multimarcas',
  fallback: null,
  fact: 'memória',
  knowledge: 'conhecimento',
  guardrail: 'guardrail',
  guardrails: 'guardrails',
  entry: 'regra',
  entries: 'regras',
  listing: 'listagem',
  link: 'link',
  report: 'relatório',
  request: 'solicitação',
  requests: 'solicitações',
  details: 'detalhes',
  info: 'resumo',
  information: 'informações',
  system: 'sistema',
  files: 'arquivos',
  bills: 'faturas',
  bill: 'fatura',
  financial: 'financeiros',
  parameters: 'parâmetros',
  activity: 'atividades',
  logs: 'registros',
  apolice: 'apólice',
  data: 'dados',
  nota: 'nota',
  fiscal: 'fiscal',
  chassi: 'chassi',
  period: 'período',
  options: 'opções',
  method: 'forma',
  cancellation: 'cancelamento',
  reasons: 'motivos',
  renovation: 'renovação',
  reboque: 'reboque',
  reboques: 'reboques',
  motorista: 'motorista',
  motoristas: 'motoristas',
  category: 'categoria',
  categories: 'categorias',
  claim: 'sinistro',
  document: 'documento',
  documents: 'documentos',
  collection: 'acervo',
  atualizacoes: 'atualizações',
  meta: 'metadados',
  audit: 'auditoria',
  log: 'registro',
  entity: 'entidade',
  dashboard: 'dashboard',
  charges: 'cobranças',
  chart: 'gráfico',
  extra: 'extra',
  kpi: 'KPI',
  filter: 'filtro',
  goal: 'metas',
  progress: 'progresso',
  sales: 'vendas',
  contract: 'contrato',
  type: 'tipo',
  status: 'status',
  funnel: 'funil',
  top: 'principais',
  widgets: 'widgets',
  catalog: 'catálogo',
  layout: 'layout',
  ai: 'IA',
  ia: 'IA',
  analysis: 'análise',
  financeiro: 'financeiro',
  notifications: 'notificações',
  notification: 'notificação',
  unread: 'não lidas',
  count: 'contagem',
  devices: 'dispositivos',
  read: 'lidas',
  all: 'todas',
  editing: 'edição',
  group: 'grupo',
  cnpj: 'CNPJ',
  cpf: 'CPF',
  login: 'login',
  page: 'página',
  seller: 'vendedor',
  store: 'loja',
  sellers: 'vendedores',
  stores: 'lojas',
  linked: 'vinculadas',
  uses: 'usos',
  schedule: 'programação',
  receipt: 'recebimento',
  timeline: 'linha do tempo',
  summary: 'resumo',
  edit: 'edição',
  form: 'formulário',
  full: 'completa',
  fee: 'taxa',
  price: 'preço',
  my: 'seus',
  sale: 'venda',
};

const TAIL_NOUNS = new Set([
  'detalhes',
  'resumo',
  'dados',
  'formulário',
  'registros',
  'opções',
  'parâmetros',
  'arquivos',
]);

const TOOL_TITLES: Record<string, string> = {
  search_site: 'busca no catálogo Wosi',
  browse_wosi_site: 'leitura do site wosi.com.br',
  browse_web: 'leitura de página na internet',
  get_partner: 'consulta de parceiro',
  list_coverages: 'listagem de coberturas do parceiro',
  calculate_electric_mobility: 'cotação de mobilidade elétrica',
  calculate_solar_panels: 'cotação de painéis solares',
  calculate_multimarcas: 'cotação multimarcas',
  quote_mobility_with_fallback: 'cotação de mobilidade elétrica',
  start_mobility_contract: 'contratação de mobilidade elétrica',
  create_purchase_order: 'criação de pedido',
  list_blog_articles: 'listagem pública de artigos do blog',
  get_blog_article: 'leitura do artigo publicado',
  list_blog_categories: 'categorias do blog público',
  get_my_profile: 'consulta dos seus dados',
  list_my_payment_profiles: 'consulta dos seus cartões',
  session_payment_profiles: 'consulta dos cartões da sessão',
  update_my_profile: 'atualização dos seus dados',
  list_my_insurances: 'consulta dos seus seguros',
  list_my_installments: 'consulta das suas parcelas',
  consult_installments: 'consulta das suas parcelas',
  get_policy: 'consulta da apólice',
  remember_user_fact: 'gravação na memória',
  forget_user_fact: 'exclusão da memória',
  admin_list_knowledge_entries: 'consulta da base de conhecimento',
  admin_get_knowledge_entry: 'leitura da regra da base de conhecimento',
  admin_save_knowledge_entry: 'gravação na base de conhecimento',
  admin_forget_knowledge_entry: 'exclusão da base de conhecimento',
  admin_list_rd_station_fields: 'consulta dos campos da RD Station',
  admin_get_rd_station_event_catalog:
    'consulta do catálogo de eventos da RD Station',
  admin_validate_rd_station_event: 'validação do evento da RD Station',
  admin_send_rd_station_event: 'envio de evento para a RD Station',
  admin_create_rd_station_field: 'criação de campo na RD Station',
  admin_list_guardrails: 'consulta dos guardrails da IA',
  admin_get_guardrail: 'leitura do guardrail da IA',
  admin_save_guardrail: 'gravação do guardrail da IA',
  admin_forget_guardrail: 'exclusão do guardrail da IA',
  build_listing_link: 'montagem do link da listagem',
  create_listing_report: 'geração de relatório',
  get_listing_report: 'consulta do relatório',
  invoke_agent: 'agente especialista',
  export_multimarcas_quote_pdf: 'geração do PDF da cotação multimarcas',
  admin_attach_chat_file_to_client: 'vínculo do arquivo do chat à venda',
  admin_get_client_files: 'consulta dos arquivos da venda',
  admin_list_blog_articles: 'listagem de artigos do blog',
  admin_get_blog_article: 'consulta do artigo do blog',
  admin_create_blog_category: 'criação de categoria do blog',
  admin_delete_blog_category: 'remoção de categoria do blog',
  admin_generate_blog_image_ai: 'geração de imagem',
  admin_apply_blog_studio_draft: 'atualização do rascunho do blog',
};

const NOUN_TO_GERUND: Record<string, string> = {
  listagem: 'Listando',
  consulta: 'Consultando',
  busca: 'Buscando',
  criação: 'Criando',
  cadastro: 'Cadastrando',
  atualização: 'Atualizando',
  validação: 'Validando',
  exclusão: 'Excluindo',
  remoção: 'Removendo',
  aprovação: 'Aprovando',
  reprovação: 'Reprovando',
  recusa: 'Recusando',
  cancelamento: 'Cancelando',
  ativação: 'Ativando',
  envio: 'Enviando',
  notificação: 'Notificando',
  marcação: 'Marcando',
  cobrança: 'Cobrando',
  aplicação: 'Aplicando',
  ajuste: 'Ajustando',
  preenchimento: 'Preenchendo',
  análise: 'Analisando',
  organização: 'Organizando',
  confirmação: 'Confirmando',
  reprocessamento: 'Reprocessando',
  cotação: 'Cotando',
  leitura: 'Lendo',
  início: 'Iniciando',
  gravação: 'Gravando',
  montagem: 'Montando',
  alteração: 'Alterando',
  geração: 'Gerando',
  publicação: 'Publicando',
  despublicação: 'Despublicando',
  agendamento: 'Agendando',
  arquivamento: 'Arquivando',
  reindexação: 'Reindexando',
  simulação: 'Simulando',
  vínculo: 'Vinculando',
  troca: 'Trocando',
  conversão: 'Convertendo',
  reset: 'Resetando',
};

function articleFor(noun: string): string {
  const last = noun.trim().split(/\s+/).pop() ?? '';
  if (/[çãáéíóú]$/i.test(last) || /(ção|dade|agem|oria|ência|a)$/i.test(last)) {
    return 'da ';
  }
  if (/s$/i.test(last) && !/os$/i.test(last)) {
    return 'das ';
  }
  if (/os$/i.test(last) || /ns$/i.test(last)) {
    return 'dos ';
  }
  return 'do ';
}

function joinObject(words: string[]): string {
  const parts = words.filter(Boolean);
  if (!parts.length) return '';
  if (parts.length === 1) return parts[0];

  const tail = parts[parts.length - 1];
  if (TAIL_NOUNS.has(tail)) {
    const head = parts.slice(0, -1).join(' ');
    return `${tail} ${articleFor(head)}${head}`;
  }

  return parts.join(' ');
}

export function resolveToolTitle(toolName: string): string {
  if (TOOL_TITLES[toolName]) return TOOL_TITLES[toolName];

  const parts = String(toolName ?? '')
    .replace(/^admin_/, '')
    .split('_')
    .filter(Boolean);

  if (!parts.length) return 'ferramenta';

  const verbNoun = VERB_TO_NOUN[parts[0]];
  const rest = (verbNoun ? parts.slice(1) : parts)
    .filter((token) => !SKIP_TOKENS.has(token))
    .map((token) =>
      Object.prototype.hasOwnProperty.call(TOKEN_PT, token)
        ? TOKEN_PT[token]
        : token
    )
    .filter((token): token is string => !!token);

  const object = joinObject(rest);
  if (verbNoun && object) return `${verbNoun} de ${object}`;
  if (verbNoun) return verbNoun;
  return object || 'ferramenta';
}

function looksUntranslated(label: string, toolName?: string): boolean {
  if (!label) return true;
  if (toolName && label.includes(toolName)) return true;

  const stripped = label
    .replace(/^(agora\s+)?acionando\s+/i, '')
    .replace(/^usando\s+/i, '')
    .trim();

  return /^[A-Z][a-z]+(?:\s+[A-Z][a-z]+)+$/.test(stripped);
}

function withStepDetail(
  label: string,
  step: Pick<ChatActivityStatus, 'detail'>
): string {
  const detail = step.detail?.trim();
  if (!detail || label.includes(detail)) return label;
  return `${label} · ${detail}`;
}

/** `detail` só entra como subtítulo se ainda não estiver no `label` (ex.: URL do browse). */
export function hasDistinctActivityDetail(
  step: Pick<ChatActivityStatus, 'label' | 'detail'> | null | undefined
): boolean {
  const detail = step?.detail?.trim();
  if (!detail) return false;
  return !String(step?.label ?? '').includes(detail);
}

export function processStepLabel(
  step: Pick<ChatActivityStatus, 'label' | 'toolName' | 'detail'>,
  options: { includeDetail?: boolean } = {}
): string {
  const includeDetail = options.includeDetail !== false;
  const label = step.label?.trim() ?? '';
  const resolved =
    !step.toolName || !looksUntranslated(label, step.toolName)
      ? label || 'Ferramenta'
      : /^agora\s+/i.test(label)
        ? `Agora acionando ${resolveToolTitle(step.toolName)}`
        : `Acionando ${resolveToolTitle(step.toolName)}`;

  return includeDetail ? withStepDetail(resolved, step) : resolved;
}

/** Ação sem o prefixo `{agente} ·` injetado no SSE — o chip/eyebrow já identifica o agente. */
export function processStepActionLabel(
  step: Pick<ChatActivityStatus, 'label' | 'toolName' | 'agentName' | 'detail'>,
  options: { includeDetail?: boolean } = {}
): string {
  const label = processStepLabel(step, options);
  const name = step.agentName?.trim();
  if (!name) return label;

  const prefix = `${name} · `;
  if (!label.startsWith(prefix)) return label;

  return label.slice(prefix.length).trim() || label;
}

/** Parâmetros da tool em linha única muted (ex.: status scheduled · page 1). */
export function formatToolActivityDetail(detail?: string): string | undefined {
  const raw = detail?.trim();
  if (!raw) return undefined;

  const formatted: string[] = [];

  for (const part of raw.split(' · ')) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    const filtersMatch = trimmed.match(/^filters:\s*(.+)$/i);
    if (filtersMatch) {
      try {
        const parsed = JSON.parse(filtersMatch[1]) as Record<string, unknown>;
        for (const [key, value] of Object.entries(parsed)) {
          if (value === undefined || value === null || value === '') continue;
          formatted.push(`${key} ${String(value)}`);
        }
        continue;
      } catch {
        formatted.push(trimmed.replace(/^filters:\s*/i, ''));
        continue;
      }
    }

    formatted.push(trimmed.replace(/^(\w+):\s*/, '$1 '));
  }

  const text = formatted.join(' · ').trim();
  return text || undefined;
}

/** Rótulo curto no rastro — gerúndio, sem “Acionando…”. */
export function resolveToolTracePrimaryLabel(
  step: Pick<ChatActivityStatus, 'label' | 'toolName' | 'phase'>
): string {
  const toolName = step.toolName?.trim();
  if (!toolName) {
    return processStepLabel(step, { includeDetail: false })
      .replace(/^Agora\s+acionando\s+/i, '')
      .replace(/^Acionando\s+/i, '')
      .replace(/^Agora\s+/i, '')
      .trim();
  }

  if (toolName === 'admin_generate_blog_image_ai') {
    return IMAGE_GENERATION_STATUS_LABEL;
  }

  const title = resolveToolTitle(toolName);
  const patterns = [
    /^(\S+)\s+de\s+(.+)$/i,
    /^(\S+)\s+do\s+(.+)$/i,
    /^(\S+)\s+da\s+(.+)$/i,
    /^(\S+)\s+dos\s+(.+)$/i,
    /^(\S+)\s+das\s+(.+)$/i,
  ];

  for (const pattern of patterns) {
    const match = title.match(pattern);
    if (!match) continue;
    const gerund = NOUN_TO_GERUND[match[1].toLowerCase()];
    if (gerund) return `${gerund} ${match[2]}`;
  }

  return processStepLabel(step, { includeDetail: false })
    .replace(/^Agora\s+acionando\s+/i, '')
    .replace(/^Acionando\s+/i, '')
    .replace(/^Concluí\s+/i, '')
    .replace(/^Agora\s+/i, '')
    .trim();
}
